import { Button, MenuItem, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { RemoveHotList } from "../../CBComponents/Icons";
import { integrationService } from "client-app-core";
import { useSelector } from "react-redux";
import { generateDirStyles } from "orion-components/SharedComponents/GenerateDirStyles/GenerateDirStyles";
import { getDir } from "orion-components/i18n/Config/selector";
import { Translate, getTranslation } from "orion-components/i18n";
import { styled } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { createUserFeedback } from "orion-components/Dock/Actions/index.js";
import CircularProgress from "@mui/material/CircularProgress";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
	backgroundColor: "#2C2D2F",
	color: "#fff",
	fontSize: "12px",
	minHeight: "30px",
	height: "30px",
	fontFamily: "Roboto",
	"&:hover": {
		backgroundColor: "#222"
	},
	"&.Mui-selected": {
		backgroundColor: "#2B343A"
	},
	"&.Mui-selected:hover": {
		backgroundColor: "#2A2B2D"
	}
}));

const hotListInputs = [
	{
		key: "plateText",
		labelKey: "plateText",
		required: true
	}
];

const hotListFields = [
	{ label: "plateNumber", key: "text", minWidth: 65, type: "text", contentWidth: 65 },
	{ label: "watchListGroup", key: "watchListGroup", minWidth: 71, type: "select", contentWidth: 70 }
];

const baseStyles = {
	container: {
		paddingRight: 10,
		paddingLeft: 15
	},
	hotListHeader: {
		fontFamily: "Roboto",
		fontStyle: "normal",
		fontWeight: 400,
		fontSize: "14px",
		lineHeight: "16px",
		color: "#FFFFFF"
	},
	hotListUIControlsHeader: { marginBottom: 10, marginRight: 10 },
	hotListItemContainer: {
		width: 400,
		minHeight: 52,
		backgroundColor: "#494D53",
		borderRadius: 5,
		padding: "8px 10px",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: 8,
		marginBottom: 12
	},
	hotListItemWrapper: {
		display: "flex",
		alignItems: "center",
		flex: 1,
		gap: 16,
		minWidth: 0
	},
	hotListFieldWrapper: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "flex-start",
		gap: "4px",
		flex: 1,
		minWidth: 0
	},
	hotListFieldHeader: {
		color: "#B5B9BE",
		fontFamily: "Roboto",
		fontSize: "10px",
		fontStyle: "normal",
		fontWeight: 400,
		lineHeight: "normal",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		overflow: "hidden"
	},
	hotListValues: {
		color: "#FFF",
		fontFamily: "Roboto",
		fontSize: "12px",
		fontStyle: "normal",
		fontWeight: 400,
		lineHeight: "normal",
		overflow: "hidden",
		width: "100%",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap"
	},
	removeButton: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
		width: 18,
		height: 18,
		marginLeft: 6
	},
	textFieldInputProps: {
		fontFamily: "Roboto",
		fontWeight: 400,
		fontSize: "14px", // adjust based on Heading 3 spec
		lineHeight: "100%",
		letterSpacing: "0",
		textAlign: "center",
		color: "#FFF"
	},
	textFieldInputLabelProps: {
		fontFamily: "Roboto",
		fontSize: "12px",
		color: "#aaa"
	}
};

const getErrorMessage = (
	res,
	fallback = getTranslation("global.sharedComponents.LPRManager.csvRequestFailedTryAgain")
) => {
	// Handles shapes like:
	// { success:false, reason:{ message:{ message:"...", code:500 }, code:null } }
	// or { success:false, reason:"..." }
	// or { message:"..." }
	return res?.reason?.message?.message || res?.reason?.message || res?.reason || res?.message || fallback;
};

const LPRManager = () => {
	const isOrgAdmin = useSelector(state => state.session?.user?.profile?.admin === true);
	const dir = useSelector(state => getDir(state));

	const styles = generateDirStyles(baseStyles, dir);

	const dispatch = useDispatch();

	const [values, setValues] = useState({
		plateText: "",
		selectedWatchListGroup: ""
	});

	const [errors, setErrors] = useState({});
	const csvFileInputRef = useRef(null);
	const [isUploadingCsv, setIsUploadingCsv] = useState(false);
	const [duplicateErrorMessage, setDuplicateErrorMessage] = useState("");
	const duplicateErrorTimeoutRef = useRef(null);

	const onClickAddAsCSV = () => {
		if (isUploadingCsv) return;
		csvFileInputRef.current?.click();
	};

	const normalizeHotListResponse = response => {
		if (Array.isArray(response)) return response;
		if (Array.isArray(response?.data)) return response.data;
		return [];
	};

	const onCSVFileSelected = async e => {
		try {
			const file = e.target.files?.[0];
			if (!file) return;

			// reset input so selecting same file again triggers onChange
			e.target.value = "";

			const isCSV =
				file.type === "text/csv" ||
				file.name.toLowerCase().endsWith(".csv") ||
				file.type === "application/vnd.ms-excel";

			if (!isCSV) {
				dispatch(
					createUserFeedback(
						getTranslation("global.sharedComponents.LPRManager.uploadValidCSVFile"),
						null,
						true
					)
				);
				return;
			}

			setIsUploadingCsv(true);

			const csvText = await file.text();

			const payload = {
				csv: csvText,
				timeout: 120000
			};

			integrationService.postExternalSystemResource("veer-lpr", "addWatchListsFromCSV", payload, (err, res) => {
				// 1) Transport error
				if (err) {
					console.error("CSV upload failed:", err);
					dispatch(
						createUserFeedback(
							err?.message || getTranslation("global.sharedComponents.LPRManager.csvUploadFailed"),
							null,
							true
						)
					);
				}

				// 2) Logical error returned as a normal response
				if (res?.success === false) {
					const msg = getErrorMessage(
						res,
						getTranslation("global.sharedComponents.LPRManager.csvUploadFailed"),
						null,
						false
					);
					dispatch(createUserFeedback(msg, null, true));
				}

				// 3) Normal success handling
				if (Array.isArray(res)) {
					setHotList(res);
					dispatch(
						createUserFeedback(
							getTranslation("global.sharedComponents.LPRManager.csvUploadedSuccessfully"),
							null,
							false
						)
					);
				}

				getHotList();

				if (res?.failed?.length) {
					dispatch(
						createUserFeedback(
							getTranslation(
								"global.sharedComponents.LPRManager.csvUploadedWithError",
								res.failed.length
							),
							null,
							true
						)
					);
				} else {
					dispatch(
						createUserFeedback(
							getTranslation("global.sharedComponents.LPRManager.csvUploadedSuccessfully"),
							null,
							false
						)
					);
				}
				setIsUploadingCsv(false);
			});
		} catch (ex) {
			console.error("CSV upload unexpected error:", ex);
			setIsUploadingCsv(false);
			dispatch(
				createUserFeedback(
					ex?.message || getTranslation("global.sharedComponents.LPRManager.csvUploadFailed"),
					null,
					true
				)
			);
		}
	};

	const getWatchListGroupName = watchListGroupId => {
		if (!watchListGroupId) return "N/A";

		const matchedGroup = watchListGroups.find(group => group.id === watchListGroupId);
		return matchedGroup?.name || "N/A";
	};

	const handleChange = key => event => {
		const value = event.target.value;

		setValues(prev => ({ ...prev, [key]: value }));

		// Clear error for this field if there's a value now
		if (errors[key] && value.trim()) {
			setErrors(prev => ({ ...prev, [key]: false }));
		}
	};

	const [hotList, setHotList] = useState([]);
	const [watchListGroups, setWatchListGroups] = useState([]);
	const getHotList = () => {
		integrationService.postExternalSystemResource(
			"veer-lpr",
			"fetchWatchLists",
			{ timeout: 120000 },
			(err, response) => {
				if (err) {
					console.error(err);
					return;
				}

				setHotList(normalizeHotListResponse(response));
			}
		);
	};

	const getWatchListGroups = () => {
		integrationService.postExternalSystemResource(
			"veer-lpr",
			"fetchWatchListGroups",
			{ timeout: 120000 },
			(err, response) => {
				if (err) {
					console.error(err);
					return;
				}

				const groups = response?.data || [];

				if (groups.length === 0) return;
				setWatchListGroups(groups);
			}
		);
	};

	useEffect(() => {
		getHotList();
		getWatchListGroups();

		return () => {
			if (duplicateErrorTimeoutRef.current) {
				clearTimeout(duplicateErrorTimeoutRef.current);
			}
		};
	}, []);

	const showDuplicateErrorMessage = (plateText, groupName) => {
		if (duplicateErrorTimeoutRef.current) {
			clearTimeout(duplicateErrorTimeoutRef.current);
		}

		setDuplicateErrorMessage(
			<Translate
				value="global.sharedComponents.LPRManager.duplicatePlateError"
				primaryValue={plateText}
				secondaryValue={groupName}
			/>
		);

		duplicateErrorTimeoutRef.current = setTimeout(() => {
			setDuplicateErrorMessage("");
		}, 10000);
	};

	const addNewWatchList = () => {
		if (!values.plateText.trim()) {
			setErrors({ plateText: true });
			return;
		}

		if (!values.selectedWatchListGroup) {
			setErrors(prev => ({ ...prev, selectedWatchListGroup: true }));
			return;
		}

		const group = watchListGroups.find(g => g.id === values.selectedWatchListGroup);
		if (!group) {
			console.error("Invalid watch list group");
			return;
		}

		const plateText = values.plateText.trim();

		const payload = {
			text: plateText,
			watchlistGroupName: group.name,
			timeout: 120000
		};

		integrationService.postExternalSystemResource("veer-lpr", "addWatchList", payload, (err, res) => {
			if (err) {
				console.error("Failed to add new watch list", err);
				return;
			}

			if (res?.message === "skipped_already_exists_in_same_group") {
				showDuplicateErrorMessage(plateText, group.name);
				return;
			}

			setDuplicateErrorMessage("");
			setHotList(normalizeHotListResponse(res));

			setValues({
				plateText: "",
				selectedWatchListGroup: ""
			});
		});
	};

	const onDelete = item => {
		const watchlistGroupName = getWatchListGroupName(item.watchListGroupId);

		if (!item?.text || watchlistGroupName === "N/A") {
			console.error("Missing watchlistGroupName or plateNumber for deletion", item);
			return;
		}

		integrationService.postExternalSystemResource(
			"veer-lpr",
			"deleteWatchLists",
			{
				watchlistGroupName,
				plateNumber: item.text,
				timeout: 120000
			},
			(err, res) => {
				if (err) {
					console.error("Failed to delete license plate:", err);
					return;
				}
				setHotList(res?.data || []);
			}
		);
	};

	return (
		<div style={styles.container}>
			<p style={styles.hotListHeader}>
				<Translate value="global.sharedComponents.LPRManager.hotList" />
			</p>
			{isOrgAdmin && (
				<>
					<div style={styles.hotListUIControlsHeader}>
						<div
							style={{
								display: "flex",
								gap: 10
							}}
						>
							{hotListInputs.map((input, index) => (
								<TextField
									key={index}
									label={getTranslation(`global.sharedComponents.LPRManager.${input.labelKey}`)}
									required={input.required}
									value={values[input.key]}
									onChange={handleChange(input.key)}
									error={!!errors[input.key]} // <-- Add this
									helperText={
										errors[input.key]
											? getTranslation("global.sharedComponents.LPRManager.required")
											: ""
									}
									variant="standard"
									fullWidth
									InputProps={{
										sx: {
											...styles.textFieldInputProps,
											"& input": {
												padding: "0px 0px 10px 0px"
											},
											"&:before": {
												borderBottom: "1px solid #949597"
											},
											"&:after": {
												borderBottom: "1px solid #42a5f5"
											},
											"&:hover:not(.Mui-disabled):before": {
												borderBottom: "1px solid #42a5f5"
											}
										}
									}}
									InputLabelProps={{
										sx: styles.textFieldInputLabelProps
									}}
									sx={{
										input: {
											color: "#fff",
											fontFamily: "Roboto",
											fontSize: "12px",
											fontStyle: "normal",
											fontWeight: 400,
											lineHeight: "normal"
										},
										label: {
											color: "rgba(255, 255, 255, 0.3)",
											fontFamily: "Roboto",
											fontSize: "12px",
											fontStyle: "normal",
											fontWeight: 400,
											lineHeight: "normal"
										},

										"& .MuiFormLabel-asterisk": {
											color: "#E85858",
											fontFamily: "Roboto",
											fontSize: "12px",
											fontStyle: "normal",
											fontWeight: 400,
											lineHeight: "normal",
											leadingTrim: "both",
											textEdge: "cap"
										}
									}} data-testid="orion-components-shared-components-lprmanager-lprmanager-text-field"
								/>
							))}
						</div>
						<div style={{ marginTop: 5 }}>
							<TextField
								select
								label={getTranslation("global.sharedComponents.LPRManager.watchListGroup")}
								value={values.selectedWatchListGroup}
								onChange={e => {
									setValues(prev => ({ ...prev, selectedWatchListGroup: e.target.value }));
									setErrors(prev => ({ ...prev, selectedWatchListGroup: false }));
								}}
								required
								helperText={
									errors["selectedWatchListGroup"]
										? getTranslation("global.sharedComponents.LPRManager.required")
										: ""
								}
								error={!!errors.selectedWatchListGroup}
								variant="standard"
								fullWidth
								InputProps={{
									sx: {
										...styles.textFieldInputProps,
										"& input": {
											padding: "0px 0px 10px 0px"
										},
										"&:before": {
											borderBottom: "1px solid #949597"
										},
										"&:after": {
											borderBottom: "1px solid #42a5f5"
										},
										"&:hover:not(.Mui-disabled):before": {
											borderBottom: "1px solid #42a5f5"
										}
									}
								}}
								InputLabelProps={{
									sx: styles.textFieldInputLabelProps
								}}
								SelectProps={{
									MenuProps: {
										disablePortal: true,
										PaperProps: {
											sx: {
												backgroundColor: "#2C2D2F",
												color: "#fff",
												minWidth: "383px !important",
												width: "383px !important",
												zIndex: 99999
											}
										}
									}
								}}
								sx={{
									"& .MuiSelect-select.MuiSelect-standard.MuiInputBase-input.MuiInput-input": {
										textAlign: "left !important"
									},

									"& .MuiSelect-icon": {
										color: "#fff"
									},
									input: {
										color: "#fff",
										fontFamily: "Roboto",
										fontSize: "12px",
										fontStyle: "normal",
										fontWeight: 400,
										lineHeight: "normal"
									},
									label: {
										color: "rgba(255, 255, 255, 0.3)",
										fontFamily: "Roboto",
										fontSize: "12px",
										fontStyle: "normal",
										fontWeight: 400,
										lineHeight: "normal"
									},
									"& .MuiFormLabel-asterisk": {
										color: "#E85858",
										fontFamily: "Roboto",
										fontSize: "12px",
										fontStyle: "normal",
										fontWeight: 400,
										lineHeight: "normal",
										leadingTrim: "both",
										textEdge: "cap"
									}
								}} data-testid="orion-components-shared-components-lprmanager-lprmanager-text-field-2"
							>
								{watchListGroups?.length > 0 ? (
									watchListGroups.map(group => (
										<StyledMenuItem key={group.id} value={group.id}>
											{group.name}
										</StyledMenuItem>
									))
								) : (
									<></>
								)}
							</TextField>
						</div>
					</div>

					<Button
						onClick={addNewWatchList}
						variant="contained"
						sx={{
							width: "395px",
							height: "29px",
							backgroundColor: "#4CB5F4",
							fontSize: "12px",
							fontWeight: 700,
							fontFamily: "Roboto",
							borderRadius: "5px",
							alignSelf: "center",
							color: "#FFFFFF",
							":hover": {
								backgroundColor: "#42a5f5"
							},
							textTransform: "uppercase"
						}} data-testid="orion-components-shared-components-lprmanager-lprmanager-button"
					>
						<Translate value="global.sharedComponents.LPRManager.add" />
					</Button>
					<div style={{ marginTop: 8 }}>
						{isUploadingCsv ? (
							<div
								style={{
									width: "395px",
									height: "29px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center"
								}}
							>
								<CircularProgress size={18} data-testid="orion-components-shared-components-lprmanager-lprmanager-circular-progress" />
							</div>
						) : (
							<Button
								onClick={onClickAddAsCSV}
								variant="contained"
								sx={{
									width: "395px",
									height: "29px",
									backgroundColor: "#4CB5F4",
									fontSize: "12px",
									fontWeight: 700,
									fontFamily: "Roboto",
									borderRadius: "5px",
									alignSelf: "center",
									color: "#FFFFFF",
									":hover": { backgroundColor: "#42a5f5" },
									textTransform: "uppercase"
								}} data-testid="orion-components-shared-components-lprmanager-lprmanager-button-2"
							>
								<Translate value="global.sharedComponents.LPRManager.addAsCSV" />
							</Button>
						)}

						<input
							ref={csvFileInputRef}
							type="file"
							accept=".csv,text/csv"
							style={{ display: "none" }}
							onChange={onCSVFileSelected}
							disabled={isUploadingCsv} data-testid="orion-components-shared-components-lprmanager-lprmanager-input"
						/>
					</div>
				</>
			)}
			{duplicateErrorMessage ? (
				<div
					style={{
						marginTop: 10,
						color: "#E85858",
						fontFamily: "Roboto",
						fontSize: "12px",
						fontWeight: 400,
						lineHeight: "16px"
					}}
				>
					{duplicateErrorMessage}
				</div>
			) : null}
			<div style={{ marginTop: 25, maxHeight: "calc(100vh - 340px)", overflowY: "auto" }}>
				{hotList?.length > 0
					? hotList.map((item, index) => (
							<div style={styles.hotListItemContainer} key={index}>
								<div style={styles.hotListItemWrapper}>
									{hotListFields.map(field => (
										<div
											key={field.key}
											style={{
												...styles.hotListFieldWrapper,
												minWidth: 0
											}}
										>
											<div style={styles.hotListFieldHeader}>
												{getTranslation(`global.sharedComponents.LPRManager.${field.label}`)}
											</div>
											<div style={styles.hotListValues}>
												{field.type === "select"
													? getWatchListGroupName(item.watchListGroupId)
													: item[field.key]?.trim()
														? item[field.key]
														: "N/A"}
											</div>
										</div>
									))}
								</div>

								{isOrgAdmin && (
									<div style={styles.removeButton}>
										<span
											onClick={() => onDelete(item)}
											style={{ cursor: "pointer", display: "inline-flex" }} data-testid="orion-components-shared-components-lprmanager-lprmanager-span"
										>
											<RemoveHotList />
										</span>
									</div>
								)}
							</div>
						))
					: null}
			</div>
		</div>
	);
};

export default LPRManager;
